// React
import { Fragment, useContext, useEffect, useRef, useState } from "react"

// Components
import { BaseTable, Button, Checkbox, Loading, Modal, Select, TableHeader } from "components"
import { PicturesSection } from "pages/Inventori/Register/ItemGroup/components"

// Contexts
import { LocationContext, LocationProductContext, useOptionalCountDetailContext } from "../contexts"

// Form
import { Controller, useForm, useFormContext } from "react-hook-form"

// Third-Party Libraries
import toast from "react-hot-toast"

// Types
import type { SuccessFetch } from "types"
import type { DetailFormType, FormType } from "../utils"

// Utils
import { convertNumber, useApi, useToggle } from "utils"
import { getDifference } from "../utils"
import { useProductGroup } from "pages/Inventori/Transaction/AddNewItem/components/FormSection/utils"
import { useProductCategory } from "pages/Inventori/Register/utils"

const ALL_FILTER_OPTION = { label: "ALL", value: "" }

export function Item(): JSX.Element {
  // Hooks
  const { isActive, toggle } = useToggle(false)

  // Form
  const { trigger } = useFormContext<FormType>()

  return (
    <Fragment>
      <Button
        type="button"
        color="primary"
        onClick={() => {
          trigger(["employee_id", "location_id", "memo"]).then((res) => {
            if (res) {
              return toggle()
            }

            toast.error("All fields must be required")
          })
        }}
      >
        SELECT ITEM
      </Button>

      {isActive && <ModalSection toggle={toggle} />}
    </Fragment>
  )
}

function ModalSection(params: {
  toggle: () => void
}): JSX.Element {
  // Hooks
  const api = useApi()
  const productList = useContext(LocationProductContext)
  const { data } = useContext(LocationContext)

  const ctx = useOptionalCountDetailContext()
  const refetchCountDetail = ctx?.refetchCountDetail
  

  // Form
  const methods = useFormContext<FormType>()
  const onSubmit = (value: DetailFormType) => {
    // Vars
    const oldDetail = methods.getValues("countdetail")
    const countdetail = value.countdetail.map(item => {
      // Vars
      const data = productList.data.find(i => i.product_id === item)!
      const olditem = oldDetail.find(i => i.product_id === item)
      const soh_count = olditem?.soh_count ?? data.soh_count ?? 0
      const real_count = olditem?.real_count ?? 0

      return {
        real_count,
        soh_count,
        count_detail_id: olditem?.count_detail_id ?? null,
        mr_unit_id: olditem?.mr_unit_id ?? data.mr_unit_id,
        product_id: item,
        different: getDifference(soh_count, real_count)
      }
    })
    const { count_id, ...rest } = methods.getValues()

    //custom validation
    const nullFields: string[] = [];
    countdetail.forEach((item, index) => {
      Object.entries(item).forEach(([key, value]) => {
        if (key === "count_detail_id") return;
        if (value === null || value === undefined) {
          nullFields.push(`Row ${index + 1}: ${key}`);
        }
      });
    });

    if (nullFields.length > 0) {
      toast.error(`Field empty:\n${nullFields.join('\n')}`);
      return;
    }

    // Functions
    const callApi = () => {
      if (count_id) {
        return api.put("/invcount/update_inv_count", {
          ...rest,
          count_id,
          invcountdetail: countdetail.map(item => {
            return {
              ...item,
              count_detail_id: item.count_detail_id
            }
          })
        })
      }

      return api.post("/invcount/add", {
        ...rest,
        countdetail: countdetail
      })
    }

    return new Promise<void>((resolve, reject) => {
      toast.promise(
        callApi(),
        {
          loading: "Loading...",
          success: (res) => res.data.message,
          error: (err) => err.response.data.detail?.message ?? err.response.data.message
        }
      ).then((res: SuccessFetch<{
        payload: { count_id: number }
      }>) => {
        methods.setValue("count_id", res.data.payload.count_id)
        methods.setValue("countdetail", countdetail)
        if(refetchCountDetail)refetchCountDetail()
        params.toggle()
      }).catch(() => {
        reject(null)
      }).finally(() => {
        resolve()
      })
    })
  }

  const { control, handleSubmit, setValue, getValues } = useForm<DetailFormType>({
    defaultValues: {
      countdetail: methods.getValues("countdetail").map(item => item.product_id)
    },
  })

  const [productGroupId, setProductGroupId] = useState("")
  const [productCategoryId, setProductCategoryId] = useState("")
  const filterTouchedRef = useRef(false)
  const { dataProduct, isLoadingProduct } = useProductGroup()
  const { dataCategory, isLoadingCategory } = useProductCategory()

  useEffect(() => {
    if (!filterTouchedRef.current) return
    productList.refetchWithFilters({
      ...(productGroupId ? { mr_product_group_id: productGroupId } : {}),
      ...(productCategoryId ? { mr_product_category_id: productCategoryId } : {})
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productGroupId, productCategoryId])

  useEffect(() => {
    if (productList.isLoading) return
    const visibleIds = new Set(productList.data.map(item => item.product_id))
    const current = getValues("countdetail")
    const pruned = current.filter(id => visibleIds.has(id))
    if (pruned.length !== current.length) {
      setValue("countdetail", pruned)
    }
  }, [productList.data, productList.isLoading, getValues, setValue])

  // Vars
  const location: string = data.find(item => item.value === methods.getValues("location_id"))?.label ?? "-"
  const productGroupOptions = [ALL_FILTER_OPTION, ...dataProduct]
  const productCategoryOptions = [ALL_FILTER_OPTION, ...dataCategory]

  return (
    <Modal
      isOpen
      title="LIST ITEM"
      closeModal={params.toggle}
    >
      <Modal.Body>
        <section className="flex flex-col gap-3">
          <section className="flex items-center gap-5 flex-wrap">
            <span className="font-bold whitespace-nowrap">LOCATION: <span className="font-normal">{location}</span></span>

            <div className="flex items-center gap-2">
              <span className="font-bold whitespace-nowrap text-sm">Product Group :</span>
              <Select
                placeholder="Select..."
                controlClass="!min-h-[36px] w-[160px]"
                width
                isLoading={isLoadingProduct}
                options={productGroupOptions}
                value={
                  productGroupId
                    ? productGroupOptions.find(item => item.value === productGroupId) ?? null
                    : ALL_FILTER_OPTION
                }
                onChange={e => {
                  filterTouchedRef.current = true
                  setProductGroupId(e?.value ?? "")
                }}
              />
            </div>

            <div className="flex items-center gap-2">
              <span className="font-bold whitespace-nowrap text-sm">Product Category :</span>
              <Select
                placeholder="Select..."
                controlClass="!min-h-[36px] w-[160px]"
                width
                isLoading={isLoadingCategory}
                options={productCategoryOptions}
                value={
                  productCategoryId
                    ? productCategoryOptions.find(item => item.value === productCategoryId) ?? null
                    : ALL_FILTER_OPTION
                }
                onChange={e => {
                  filterTouchedRef.current = true
                  setProductCategoryId(e?.value ?? "")
                }}
              />
            </div>

            <div className="ml-auto">
              <Controller
                name="countdetail"
                control={control}
                render={({ field }) => {
                  const allChecked: boolean = productList.data.length > 0 && productList.data.length === field.value.length

                  return (
                    <Checkbox
                      value={allChecked}
                      onClick={() => {
                        if (allChecked) {
                          return field.onChange([])
                        }

                        return field.onChange(productList.data.map(item => item.product_id))
                      }}
                    >
                      All
                    </Checkbox>
                  )
                }}
              />
            </div>
          </section>

          <BaseTable>
            <TableHeader>
              <tr>
                <th>#</th>
                <th>ITEM</th>
                <th>DESCRIPTION</th>
                <th>PICTURE</th>
                <th>SOH</th>
                <th>UNIT</th>
              </tr>
            </TableHeader>

            <Controller
              name="countdetail"
              control={control}
              render={({ field }) => (
                <tbody>
                  {productList.isLoading || !productList.data.length ? (
                    <tr>
                      <td colSpan={6}>
                        <Loading
                          errorText="No data available"
                          loading={productList.isLoading}
                        />
                      </td>
                    </tr>
                  ) : productList.data.map((item, key) => {
                    // Vars
                    const findItem: number = field.value.findIndex(i => i === item.product_id)
                    const isChecked: boolean = findItem !== -1
                    return (
                      <tr key={key}>
                        <td>
                          <Checkbox
                            labelClassName="!justify-center"
                            value={isChecked}
                            onClick={() => {
                              // Vars
                              const value = [...field.value]

                              if (isChecked) {
                                value.splice(findItem, 1)
                              } else {
                                value.push(item.product_id)
                              }

                              return field.onChange(value)
                            }}
                          />
                        </td>

                        <td>{item.product_barcode}</td>
                        <td>{item.product_name}</td>

                        <td>
                          <section className="flex justify-center">
                            <div className="w-[400px] lg:w-[200px]">
                              <PicturesSection
                                className="h-[100px] w-full"
                                items={1}
                                dataPicture={item.pictures!}
                              />
                            </div>
                          </section>
                        </td>

                        <td>{convertNumber(item.soh_count).intoNormalAmount}</td>
                        <td>{item.mr_unit_name}</td>
                      </tr>
                    )
                  })}
                </tbody>
              )}
            />
          </BaseTable>
        </section>
      </Modal.Body>

      <Modal.Footer>
        <Button
          color="primary"
          onClick={handleSubmit(onSubmit)}
        >
          SAVE
        </Button>
      </Modal.Footer>
    </Modal>
  )
}